import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];
  #isClosing = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.#isClosing = false;
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Close mobile menu if open when opening any dialog
    if (typeof window.closeMobileMenu === 'function') {
      window.closeMobileMenu();
    }

    // Специальная логика для facets--drawer (как mobile-menu-drawer)
    const isFacetsDrawer = dialog.classList.contains('facets--drawer');
    
    if (isFacetsDrawer) {
      dialog.showModal();
      // Используем requestAnimationFrame для плавной анимации через CSS transition
      requestAnimationFrame(() => {
        dialog.classList.add('is-open');
      });
    } else {
      dialog.showModal();
    }

    this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open || this.#isClosing) return;

    this.#isClosing = true;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Специальная логика для facets--drawer (как mobile-menu-drawer)
    const isFacetsDrawer = dialog.classList.contains('facets--drawer');
    
    if (isFacetsDrawer) {
      // Используем requestAnimationFrame для синхронизации с браузером
      await new Promise(resolve => requestAnimationFrame(resolve));
      
      // Удаляем класс is-open для запуска CSS transition
      dialog.classList.remove('is-open');
      
      // Ждем окончания анимации (300ms как в mobile-menu-drawer)
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Проверяем, что dialog все еще открыт перед закрытием
      if (dialog.open) {
        dialog.close();
      }
      dialog.classList.remove('dialog-closing');
    } else {
      dialog.classList.add('dialog-closing');

      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });

      if (dialog.open) {
        dialog.close();
      }
      dialog.classList.remove('dialog-closing');
    }

    this.#isClosing = false;
    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

// Enhanced scroll lock functionality
let scrollPosition = 0;
let scrollBarWidth = 0;
let openDialogCount = 0;

function lockScroll() {
  openDialogCount++;
  
  // Only lock scroll if this is the first dialog
  if (openDialogCount === 1) {
    // Calculate scrollbar width to prevent layout shift
    scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    // Save current scroll position
    scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    // Apply styles to prevent scrolling
    document.documentElement.setAttribute('scroll-lock', '');
    document.body.style.top = `-${scrollPosition}px`;
    document.body.style.paddingRight = `${scrollBarWidth}px`;
  }
}

function unlockScroll() {
  openDialogCount = Math.max(0, openDialogCount - 1);
  
  // Only unlock scroll if no dialogs are open
  if (openDialogCount === 0) {
    // Remove scroll lock
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.top = '';
    document.body.style.paddingRight = '';
    
    // Restore scroll position
    window.scrollTo(0, scrollPosition);
  }
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        // Check if this is the main mobile menu container
        const isMainMobileMenu = event.target.id === 'Details-menu-drawer-container' || 
                               event.target.classList.contains('menu-drawer-container');

        // Check if this is a submenu inside mobile menu
        const isSubmenuInMobileMenu = event.target.closest('.menu-drawer') && 
                                    event.target !== document.getElementById('Details-menu-drawer-container');

        if (open) {
          // Only lock scroll for main containers (mobile menu, dialogs) not for submenus
          if (isMainMobileMenu || !isSubmenuInMobileMenu) {
            lockScroll();
          }
        } else {
          // Only unlock for main containers
          if (isMainMobileMenu || !isSubmenuInMobileMenu) {
            unlockScroll();
          }
        }
      }
    }
  },
  { capture: true }
);

// Handle window resize and orientation change
function handleResize() {
  if (document.documentElement.hasAttribute('scroll-lock')) {
    // Recalculate scrollbar width
    const newScrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (newScrollBarWidth !== scrollBarWidth) {
      scrollBarWidth = newScrollBarWidth;
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }
  }
}

window.addEventListener('resize', handleResize);
window.addEventListener('orientationchange', handleResize);
